import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MapPinIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const SECTION_LABELS = {
  hero: 'Hero Section',
  features_strip: 'Features Strip',
  youtube_videos: 'YouTube Videos',
  why_choose: 'Why Choose',
  local_insights: 'Local Insights',
  loan_products: 'Loan Products',
  how_to_apply: 'How To Apply',
  eligibility: 'Eligibility',
  customer_stories: 'Customer Stories',
  visit_us: 'Visit Us / Map',
  gallery: 'Gallery',
  coverage: 'Coverage Areas',
  faq: 'FAQ Section',
  cta: 'CTA Section',
   seo: 'SEO Settings'
};

const AdminGMBBranchManagement = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({ branch_name: '', branch_slug: '', url_path: '' });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/gmb-branches');
      if (response.data.success) {
        setBranches(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching GMB branches:', error);
      toast.error('Failed to load branch pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleNameChange = (name) => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setFormData({
      branch_name: name,
      branch_slug: slug,
      url_path: `/home-loan-in-${slug}`
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.branch_name.trim()) {
      toast.error('Branch name is required');
      return;
    }
    try {
      setSubmitting(true);
      const response = await axios.post('/api/gmb-branches', formData);
      if (response.data.success) {
        toast.success(`Branch page "${formData.branch_name}" created successfully!`);
        setShowCreateModal(false);
        setFormData({ branch_name: '', branch_slug: '', url_path: '' });
        fetchBranches();
      }
    } catch (error) {
      console.error('Error creating branch:', error);
      toast.error(error.response?.data?.message || 'Failed to create branch page');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      branch_name: branch.branch_name,
      branch_slug: branch.branch_slug,
      url_path: branch.url_path
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await axios.put(`/api/gmb-branches/${editingBranch.id}`, formData);
      toast.success('Branch page updated successfully!');
      setShowEditModal(false);
      setEditingBranch(null);
      setFormData({ branch_name: '', branch_slug: '', url_path: '' });
      fetchBranches();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/gmb-branches/${id}`);
      toast.success('Branch page deleted');
      setShowDeleteConfirm(null);
      fetchBranches();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleActive = async (branch) => {
    try {
      await axios.put(`/api/gmb-branches/${branch.id}`, {
        is_active: !branch.is_active
      });
      toast.success(`Branch ${branch.is_active ? 'deactivated' : 'activated'}`);
      fetchBranches();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">GMB Branch Pages</h1>
          <p className="text-slate-500 mt-1">Create and manage dynamic branch landing pages</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={fetchBranches}
            className="inline-flex items-center gap-2 bg-white text-slate-700 border border-slate-300 px-4 py-2.5 rounded-xl hover:bg-slate-50 transition-colors"
          >
            <ArrowPathIcon className="w-5 h-5" />
            Refresh
          </button>
          <button
            onClick={() => {
              setFormData({ branch_name: '', branch_slug: '', url_path: '' });
              setShowCreateModal(true);
            }}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
          >
            <PlusIcon className="w-5 h-5" />
            Create New Branch Page
          </button>
        </div>
      </div>

      {/* Branch Cards */}
      {branches.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-16 text-center">
          <MapPinIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Branch Pages Yet</h3>
          <p className="text-slate-500 mb-6">Create your first dynamic branch landing page.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-indigo-600 text-white px-6 py-3 rounded-xl hover:bg-indigo-700 transition-colors"
          >
            + Create First Branch Page
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {branches.map((branch) => (
            <div key={branch.id} className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <MapPinIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">{branch.branch_name}</h3>
                      <p className="text-indigo-200 text-xs">{branch.url_path}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${branch.is_active ? 'bg-green-400/20 text-green-100' : 'bg-red-400/20 text-red-100'}`}>
                    {branch.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              {/* Card Body - Section List */}
              <div className="p-5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Sections (click to edit)</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(SECTION_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => navigate(`/admin/gmb-branch-pages/${branch.id}/sections/${key}`)}
                      className="text-left text-xs bg-slate-50 hover:bg-indigo-50 hover:text-indigo-700 text-slate-600 px-3 py-2 rounded-lg transition-colors truncate"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Card Footer */}
              <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between bg-slate-50/50">
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(branch)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Edit">
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleToggleActive(branch)} className="p-2 text-slate-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors" title="Toggle Active">
                    <EyeIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => setShowDeleteConfirm(branch.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
                <a href={branch.url_path} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                  Preview →
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-slate-800 mb-1">Create New Branch Page</h2>
            <p className="text-sm text-slate-500 mb-6">This will duplicate Madurai's 13 sections as a starting template and replace the city name automatically.</p>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Branch Name *</label>
                <input
                  type="text"
                  value={formData.branch_name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. Theni"
                  className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">URL Slug</label>
                <input
                  type="text"
                  value={formData.branch_slug}
                  onChange={(e) => {
                    const cleanSlug = e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                    setFormData({
                      ...formData,
                      branch_slug: e.target.value,
                      url_path: `/home-loan-in-${cleanSlug}`
                    });
                  }}
                  className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Full URL Path</label>
                <input
                  type="text"
                  value={formData.url_path}
                  onChange={(e) => setFormData({ ...formData, url_path: e.target.value })}
                  className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm bg-slate-50 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 bg-slate-100 text-slate-700 py-2.5 rounded-xl font-medium hover:bg-slate-200 transition-colors">Cancel</button>
                <button type="submit" disabled={submitting} className="flex-1 bg-indigo-600 text-white py-2.5 rounded-xl font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50">
                  {submitting ? 'Creating...' : 'Create Branch'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEditModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-slate-800 mb-4">Edit Branch Page</h2>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Branch Name</label>
                <input type="text" value={formData.branch_name} onChange={(e) => setFormData({ ...formData, branch_name: e.target.value })} className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">URL Slug</label>
                <input type="text" value={formData.branch_slug} onChange={(e) => setFormData({ ...formData, branch_slug: e.target.value })} className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Full URL Path</label>
                <input type="text" value={formData.url_path} onChange={(e) => setFormData({ ...formData, url_path: e.target.value })} className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowEditModal(false); setEditingBranch(null); }} className="flex-1 bg-slate-100 text-slate-700 py-2.5 rounded-xl font-medium hover:bg-slate-200">Cancel</button>
                <button type="submit" disabled={submitting} className="flex-1 bg-indigo-600 text-white py-2.5 rounded-xl font-medium hover:bg-indigo-700 disabled:opacity-50">
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrashIcon className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Delete Branch Page?</h3>
            <p className="text-sm text-slate-500 mb-6">This will permanently delete this branch page and all its section data. This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(null)} className="flex-1 bg-slate-100 text-slate-700 py-2.5 rounded-xl font-medium hover:bg-slate-200">Cancel</button>
              <button onClick={() => handleDelete(showDeleteConfirm)} className="flex-1 bg-red-600 text-white py-2.5 rounded-xl font-medium hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGMBBranchManagement;
